import { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchMe } from "../services/graphMe";

// ═══════════════════════════════════════════════════════════════════════════════
// USER PROFILE CONTEXT
// ═══════════════════════════════════════════════════════════════════════════════
// Fetches the signed-in user's Graph profile (name, email, phone, jobTitle)
// once at mount, then shares it across the app. Components that need to know
// "who's signed in" — the LOI sender block, Settings account card, etc. —
// read from this instead of calling Graph themselves.

const UserProfileContext = createContext({
  profile: null,
  loading: true,
  error: null,
  refresh: () => {},
});

export function UserProfileProvider({ children }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    if (!account) return;
    setLoading(true);
    setError(null);
    try {
      const me = await fetchMe(instance);
      setProfile({
        // Fields match the `sender` shape consumed by generateLoiBody():
        name: me.displayName || account.name || account.username,
        email: me.email || account.username || "",
        phone: me.phone || "",
        jobTitle: me.jobTitle || "",
      });
    } catch (e) {
      console.error("Failed to fetch /me:", e);
      setError(e);
      // Fall back to MSAL account claims so the app still works offline from Graph.
      setProfile({
        name: account.name || account.username,
        email: account.username || "",
        phone: "",
        jobTitle: "",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId]);

  return (
    <UserProfileContext.Provider value={{ profile, loading, error, refresh: load }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  return useContext(UserProfileContext);
}
